import React, { createContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import * as uuid from "uuid"
import { Lead } from "models/Lead";
import { LeadAPI } from "apis/LeadAPI";
import { useSearchParams } from "react-router-dom";
import { GeographyContext } from "GeographyContextWrapper";
import { isProd } from "./constants";
import { knowledgeEngineDataPrefix } from "LeadProbateContextWrapper";
import { probateApi } from "apis";
import { ProbateField } from "models/ProbateField";

const leadKeyCookie = "lead_key"
const leadKeyCookieExpiryDays = 3/24 // 3 hours

interface Props {
  children: JSX.Element;
  autoRefreshLeadKeyExpiry?:boolean,
  migrationOnly?: boolean, // If we are going to use this info only to migrate lead data to user data
}

export interface LeadContextInterface {
  lead?: Lead,
  leadData?: {[key:string]:any},
  saveLeadData?: (leadKey:string, data: {[key:string]:any}) => Promise<void>
  deleteLeadCookie? : () => void,
  doneLoadingLead: boolean,
}

export const LeadContext = createContext<LeadContextInterface>({
  lead: undefined,
  leadData: undefined,
  saveLeadData: undefined,
  deleteLeadCookie: undefined,
  doneLoadingLead: false,
})

export const LeadContextWrapper = ({ children, migrationOnly = false, autoRefreshLeadKeyExpiry = true }: Props): JSX.Element => {
  const [lead, setLead] = React.useState<Lead|undefined>()
  const [leadData, setLeadData] = React.useState<{[key:string]:any}|undefined>()
  const [searchParams] = useSearchParams();
  const [doneLoadingLead, setDoneLoadingLead] = useState<boolean>(true)
  const { states } = React.useContext(GeographyContext)

  const loadLeadKey = ():string|undefined => {
    return Cookies.get(leadKeyCookie) 
  }

  const saveLeadKey = (key:string):string => {
    if(migrationOnly) {
      return ""
    }

    Cookies.set(leadKeyCookie, key, {
      expires: leadKeyCookieExpiryDays, 
    }) 
    return key
  }

  const deleteLeadCookie = () => {
    Cookies.remove(leadKeyCookie)
    setLead(undefined)
    setLeadData(undefined)
  }

  const refreshLeadKeyExpiry = () => {
    saveLeadKey(loadLeadKey() || lead?.key || "")
  }

  useEffect(() => {
    const fetchLeadAndStartRefresher = async () => {
      try {
        console.log("fetching lead")

        // Pull from cookie
        let leadKey = loadLeadKey()

        // Pull from url if we don't have in cookie
        if(!leadKey) {
          leadKey = searchParams.get("lead_key") || undefined
        }

        // Pull rest of data from api
        let foundLead = false
        if(leadKey) {
          const lead = await LeadAPI.getLead(leadKey)
          if(lead) {
            console.log("found lead key and lead from api")
            foundLead = true
            setLead(lead)
          }
        } 

        if(migrationOnly) {
          setDoneLoadingLead(true)
          return
        }

        // New lead, never seen before
        if(!foundLead) {
          console.log("didn't find lead key and/or lead, creating new lead. Current key is " + leadKey)
          const lead = new Lead()
          lead.key = leadKey || uuid.v4()
          await LeadAPI.upsertLead(lead.toJson())
          saveLeadKey(lead.key)
          setLead(lead)
        }

        setDoneLoadingLead(true)
      } catch(e) {
        console.error("unable to fetch lead from api, " + (e as any)?.message)
      }

      // After lead is pulled and saved, start auto-refreshing cookie expiry as long as browser is active
      if(autoRefreshLeadKeyExpiry) {
        setInterval(refreshLeadKeyExpiry, 60000)
      }
    }

    fetchLeadAndStartRefresher()
  }, [])

  
  // Load lead data from api
  useEffect(() => {
    const fetchLeadData  = async () => {
      try {
        if(!lead?.key) {
          !isProd && !migrationOnly && console.warn("no lead key yet")
          return
        }
        if(!states) {
          !isProd && console.warn("no states loaded yet")
          return
        }

        console.log("fetching lead data")

        const leadData = await LeadAPI.getLeadData(lead.key)
        
        // If we were passed a location, find the related country/state and save those on the lead data
        // if we don't have them yet
        let updateDB = false
        const location = searchParams.get("location")
        const state = states?.find(s => {
          return s.fullName === location
        })
        if(!leadData["country_id"] && state?.countryId) {
          leadData["country_id"] = state?.countryId
          updateDB = true
        }
        if(!leadData["state_id"] && state?.id ) {
          leadData["state_id"] = state?.id 
          updateDB = true
        }

        // Set name if we don't have it yet
        const name = searchParams.get("name")
        if(name) {
          leadData["name_of_deceased"] = name
          updateDB = true
        }

        // Set date of death if we don't have it yet
        const dodString = searchParams.get("dod") || "" // MM-dd-YYYY format
        const dateParts = dodString.split("-")
        if(dateParts.length === 3 && !leadData[knowledgeEngineDataPrefix + "deceased_date"]) {
          const z1 = dateParts[0].toString().length === 1 ? "0" : ""
          const z2 = dateParts[1].toString().length === 1 ? "0" : ""
          const dateString = `${dateParts[2]}-${z1}${dateParts[0]}-${z2}${dateParts[1]}T23:00:00.000Z`
          const dateField = new ProbateField(
            "deceased_date",
            "date",
            "When did someone pass away?",
            undefined,
            "deceased",
            "Passing date",
            dateString,
            undefined,
            false,
            undefined
          )
          leadData[knowledgeEngineDataPrefix + "deceased_date"] = dateField
          await probateApi.saveFieldToKnowledgeEngineForLead(lead.key, dateField)
          
          // Go ahead and answer this question too since we often ask it twice and it's weird
          const dayDiff = Math.floor((Date.now() - Date.parse(dateString)) / 86400000);
          console.log("Day diff is " + dayDiff.toString())
          if(Number.isSafeInteger(dayDiff)) {
            const dayDiffField = new ProbateField(
              "days_since_passing",
              "number",
              "How many days ago did the deceased pass?",
              undefined,
              "deceased",
              "Days since passing",
              dayDiff,
              undefined,
              true,
              ["deceased_date"]
            )
            leadData[knowledgeEngineDataPrefix + "days_since_passing"] = dayDiffField
            await probateApi.saveFieldToKnowledgeEngineForLead(lead.key, dayDiffField)
          }

          updateDB = true
        }
        
        if(updateDB) {
          saveLeadData(lead.key, leadData)
        } else {
          setLeadData(leadData)
        }

        console.log("done fetching lead data")
      } catch(e) {
        console.error("unable to fetch lead data from api, " + (e as any)?.message)
      }
    }
    fetchLeadData()
  }, [lead?.key, states?.length])

  async function saveLeadData(leadKey:string, leadData: {[key:string]: any}) {
    console.log("saving lead data")
    
    await LeadAPI.upsertLeadData(leadKey, leadData)
    setLeadData(leadData)
  }

  return (
    <LeadContext.Provider
      value={{
       lead,
       leadData,
       saveLeadData,
       deleteLeadCookie,
       doneLoadingLead,
      }}
    >
      {children}
    </LeadContext.Provider>
  );
};
